/**
 * React hand-over for the suite's served top bar (`<suite-topbar>`, loaded
 * from the hub as chrome.js). The bar's layout, right cluster, hide/show and
 * chat entry live in chrome.js — edited once for every application; the
 * handshake (property hand-over, chrome:* events, right-cluster actions)
 * lives in the shared `@suite/topbar` adapter. THIS file is only yap's config.
 *
 * The suite bar carries the workspace navigation and the File menu (the
 * document / persistence / export / analysis / settings commands — the
 * Toolbar's own File menu button was folded in here) and leads its right
 * cluster with the Collaborate / Plugins actions in EVERY workbench — the
 * Toolbar's own collab / plugins triggers were folded in here, so the bar
 * reads the same no matter which view mode is active.
 */

import { createElement, useEffect, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { useNavigate } from "react-router-dom";
import i18n from "i18next";
import { Box, Ruler, Shapes, PencilRuler, FilePlus, FolderOpen, Save, FileText, Download, AlertTriangle, Activity, CheckCircle, List, Settings, Command } from "lucide-react";
import { FiUsers, FiPackage } from "react-icons/fi";
import { useMode } from "../state/store";
import { useAppStore } from "../state";
import { useCommandManager } from "../state/commandStore";
import { useTranslation } from "react-i18next";
import { useCollaboration } from "../collaboration";
import { useHubAuth } from "../gate/useHubAuth";
import { SuiteTopbarAdapter } from "@suite/topbar";
import { openYapSettings } from "../settings/openYapSettings";
import type { TopbarFileMenuSection, TopbarNavItem } from "@suite/topbar";

const MODE_ICONS: Record<string, typeof Box> = {
  "/bim": Box,
  "/2d": Ruler,
  "/3d": Shapes,
  "/sketch": PencilRuler,
};

const svgOf = (Icon: unknown, size = 14, color?: string) =>
  renderToStaticMarkup(
    createElement(Icon as never, color ? { size, color } : { size })
  );

export function SuiteTopbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // The suite's shared identity: the account button (left of Settings) and
  // its dropdown show the signed-in user across every application.
  const { user } = useHubAuth();
  const mode = useMode();
  // Appearance is owned by the suite runtime (window.SuiteTheme); the bar's
  // quick toggle (left of the account button) and the live mode both ride it.
  // Handing in a non-empty themeModes list makes chrome.js render the toggle,
  // which fires chrome:theme and calls SuiteTheme.setMode — ThemeSync bridges
  // the flip onto Chakra, so no theme handler is needed on this side.
  const [theme, setThemeState] = useState<string>(() => window.SuiteTheme?.mode ?? "dark");
  useEffect(() => {
    const unsubscribe = window.SuiteTheme?.onChange((state) => setThemeState(state.mode));
    return unsubscribe;
  }, []);
  // The center prompt is the suite's shared AI agent input (chrome.js routes an
  // unclaimed submit to the chat dock → hub backend); the app's own command line
  // below keeps owning command entry.
  // SettingsHost listens for the yap:open-settings event
  const settingsEnabled = true;

  // Every workbench leads the suite bar's right cluster with the Collaborate /
  // Plugins entries (ahead of the bell / search buttons) — the same actions
  // the app's Toolbar used to carry at its right end. The collaborate icon
  // keeps the connected green tint its toolbar trigger had.
  const { isConnected } = useCollaboration();
  const setCollabRoomOpen = useAppStore((s) => s.setCollabRoomOpen);
  const setPluginManagerOpen = useAppStore((s) => s.setPluginManagerOpen);

  // Language and mode labels are resolved per render so a language switch
  // (i18next re-render) is reflected in the served bar immediately.
  const LANGUAGES = [
    { code: "en", label: t('ui.topbar.english') },
    { code: "tr", label: t('ui.topbar.turkce') },
  ];

  const MODES: Array<{ path: string; label: string; icon: typeof Box }> = [
    { path: "/sketch", label: t('ui.topbar.sketch'), icon: MODE_ICONS["/sketch"] as typeof PencilRuler },
    { path: "/2d", label: t('ui.topbar.2d'), icon: MODE_ICONS["/2d"] as typeof Ruler },
    { path: "/3d", label: t('ui.topbar.3d'), icon: MODE_ICONS["/3d"] as typeof Shapes },
    { path: "/bim", label: t('ui.topbar.bim'), icon: MODE_ICONS["/bim"] as typeof Box },
  ];

  const nav = (): TopbarNavItem[] =>
    MODES.map((m) => ({
      key: m.path,
      label: m.label,
      icon: svgOf(m.icon, 16),
      active: mode === m.path.slice(1),
      onClick: () => navigate(m.path),
    }));

  const actions = (): TopbarNavItem[] => [
    {
      key: "collaborate",
      icon: svgOf(FiUsers, 16, isConnected ? "var(--chakra-colors-green-400)" : undefined),
      title: t('collab.collaboration'),
      onClick: () => setCollabRoomOpen(true),
    },
    {
      key: "plugins",
      icon: svgOf(FiPackage, 16),
      title: t('plugins.plugins'),
      onClick: () => setPluginManagerOpen(true),
    },
  ];

  // The File menu is the suite bar's own menu (chrome.js portals it to the
  // viewport as an overlay): document, persistence, export, analysis and
  // settings commands moved in from the Toolbar's FileMenu. Every row runs
  // through the shared command store, and its hint reflects the live
  // (possibly user-assigned) binding. No theme row: the bar's right cluster
  // owns the Appearance toggle.
  const executeCommand = useCommandManager((s) => s.executeCommand);
  const commands = useCommandManager((s) => s.commands);
  const shortcutHint = (cmdId: string) => commands[cmdId]?.shortcut || undefined;

  const fileMenu = (): TopbarFileMenuSection[] => [
    {
      label: t('ui.topbar.document'),
      items: [
        { key: "newProject", label: t('ui.topbar.newProject'), icon: svgOf(FilePlus), hint: shortcutHint("newProject"), onClick: () => executeCommand("newProject") },
        { key: "openProject", label: t('ui.topbar.openProject'), icon: svgOf(FolderOpen), hint: shortcutHint("openProject"), onClick: () => executeCommand("openProject") },
      ],
    },
    {
      label: t('ui.topbar.persistence'),
      items: [
        { key: "saveProject", label: t('ui.topbar.saveProject'), icon: svgOf(Save), hint: shortcutHint("saveProject"), onClick: () => executeCommand("saveProject") },
        { key: "saveProjectAs", label: t('ui.topbar.saveProjectAs'), icon: svgOf(Save), hint: shortcutHint("saveProjectAs"), onClick: () => executeCommand("saveProjectAs") },
        { key: "saveAsTemplate", label: t('ui.topbar.saveAsTemplate'), icon: svgOf(FileText), hint: shortcutHint("saveAsTemplate"), onClick: () => executeCommand("saveAsTemplate") },
      ],
    },
    {
      label: t('ui.topbar.export'),
      items: [
        { key: "exportObj", label: t('ui.topbar.exportAsObj'), icon: svgOf(Download), hint: shortcutHint("exportObj"), onClick: () => executeCommand("exportObj") },
        { key: "exportSvg", label: t('ui.topbar.exportAsSvg'), icon: svgOf(Download), hint: shortcutHint("exportSvg"), onClick: () => executeCommand("exportSvg") },
      ],
    },
    {
      label: t('ui.topbar.analysis'),
      items: [
        { key: "executeClashDetection", label: t('ui.topbar.runClashDetection'), icon: svgOf(AlertTriangle), hint: shortcutHint("executeClashDetection"), onClick: () => executeCommand("executeClashDetection") },
        { key: "executeRunFEA", label: t('ui.topbar.runStructuralAnalysis'), icon: svgOf(Activity), hint: shortcutHint("executeRunFEA"), onClick: () => executeCommand("executeRunFEA") },
        { key: "executeValidateMVD", label: t('ui.topbar.validateIfcMvd'), icon: svgOf(CheckCircle), hint: shortcutHint("executeValidateMVD"), onClick: () => executeCommand("executeValidateMVD") },
        { key: "executeGenerateSchedules", label: t('ui.topbar.generateSchedules'), icon: svgOf(List), hint: shortcutHint("executeGenerateSchedules"), onClick: () => executeCommand("executeGenerateSchedules") },
      ],
    },
    {
      label: t('ui.topbar.settings'),
      items: [
        { key: "openSettings", label: t('ui.topbar.settings2'), icon: svgOf(Settings), hint: shortcutHint("openSettings"), onClick: () => executeCommand("openSettings") },
        { key: "openShortcuts", label: t('ui.topbar.keyboardShortcuts'), icon: svgOf(Command), hint: shortcutHint("openShortcuts"), onClick: () => executeCommand("openShortcuts") },
      ],
    },
  ];

  return (
    <SuiteTopbarAdapter
      app="yap"
      nav={nav}
      actions={actions}
      fileMenu={fileMenu}
      identity={user ? { name: user.name, sublabel: user.email, avatarUrl: user.avatarUrl ?? undefined } : null}
      theme={theme}
      themeModes={["light", "dark"]}
      languages={LANGUAGES}
      language={i18n.resolvedLanguage ?? "en"}
      badge={0}
      notifications={false}
      settingsEnabled={settingsEnabled}
      events={{
        // Settings-window plan: the in-app dialog by default; the shared
        // two-column window when the user opts in (the panel's toggle).
        settings: () => openYapSettings(),
        // The account menu's "Profile & Account" lands on the settings
        // surface — the app's one identity surface.
        user: () => openYapSettings(),
        // The bar's lead Help Center button opens the app's own panel — the
        // same appStore flag the ⌘K / Ctrl+K chord raises.
        helpCenter: () => useAppStore.getState().setHelpCenterOpen(true),
        language: ({ code }) => {
          if (code) void i18n.changeLanguage(code);
        },
      }}
    />
  );
}
